import React, { useEffect, useMemo, useState } from "react";
import Image from "next/image";
import { useRouter } from "next/router";
import axios from "axios";
import Cookies from "js-cookie";

// Logo imports (adjust paths as needed)
import logo1 from "../../public/1.webp";
import logo2 from "../../public/2.webp";
import logo3 from "../../public/4.webp";

// helper to make camelCase or snake_case keys human readable
const insertSpacesInCamelCase = (str = "") =>
  String(str)
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/[_-]/g, " ")
    .replace(/&/g, " & ")
    .trim();

const checkIcon = <span className="text-green-500 text-[15px]">✔</span>;
const crossIcon = <span className="text-red-500 text-[15px]">✖</span>;

const tierLogos = {
  pgfree: logo1,
  pgbasic: logo2,
  pgpremium: logo3,
};

export default function Plans() {
  const router = useRouter();
  const [remotePricing, setRemotePricing] = useState(null);
  const API_URL = process.env.NEXT_PUBLIC_API_URL || "";

  useEffect(() => {
    const fetchPricing = async () => {
      try {
        if (!API_URL) return;
        const res = await axios.get(`${API_URL}/api/fetch-pgservices-data`);
        if (res?.data?.data?.length > 0) {
          const fetched = res.data.data[0];
          // remove internal ids if present
          const copy = JSON.parse(JSON.stringify(fetched));
          delete copy._id;
          if (copy.priceDetails) delete copy.priceDetails._id;
          if (copy.features) delete copy.features._id;
          setRemotePricing(copy);
        }
      } catch (err) {
        console.error("Failed to fetch PG pricing for Plans:", err);
      }
    };

    fetchPricing();
  }, [API_URL]);

  const handleCheckout = (tier) => {
    const priceFromApi = remotePricing?.priceDetails?.[tier];
    const price = typeof priceFromApi !== "undefined" ? priceFromApi : 0;

    // Collect all features for this tier as an array
    const featuresArray = featureRows
      .filter(row => row[tier] === "Yes")
      .map(row => row.feature);

    // Check if user is logged in by checking for authToken
    const authToken = Cookies.get("authToken");
    const isLoggedIn = authToken && authToken !== "undefined" && authToken !== "null" && authToken.trim() !== "";

    // Build a checkout return URL (used when redirecting to login)
    const checkoutQuery = `tier=${encodeURIComponent(tier)}&price=${encodeURIComponent(price)}&features=${encodeURIComponent(JSON.stringify(featuresArray))}`;
    const checkoutPath = `/checkout?${checkoutQuery}`;

    if (!isLoggedIn) {
      // Store a small backup payload in sessionStorage so login page can forward after sign-in (best-effort)
      try {
        sessionStorage.setItem('checkoutAfterLogin', JSON.stringify({ tier, price, features: featuresArray }));
      } catch (e) {
        // ignore storage errors
      }

  // Redirect to the fixed portal login host
  const loginHost = 'https://portal.neetsupport.com';
  const returnUrl = window.location.origin + checkoutPath; // absolute return URL back to this app
  const loginUrl = `${loginHost}/login?next=${encodeURIComponent(returnUrl)}`;
  window.location.href = loginUrl;
      return;
    }

    // User is logged in, go to checkout immediately
    router.push({
      pathname: '/checkout',
      query: {
        tier: tier,
        price: price,
        features: JSON.stringify(featuresArray), // Send as JSON string
      },
    });
  };

  // Default fallback pricing
  const priceBasic = typeof remotePricing?.priceDetails?.pgbasic !== "undefined"
    ? remotePricing.priceDetails.pgbasic
    : 1980;
  const pricePremium = typeof remotePricing?.priceDetails?.pgpremium !== "undefined"
    ? remotePricing.priceDetails.pgpremium
    : 29980;

  // Build feature rows from remotePricing
  const featureRows = useMemo(() => {
    if (!remotePricing || !remotePricing.features) {
      return [];
    }

    // Define custom section order: tools, analysis, postExamMentorship, others...
    const sectionOrder = ["tools", "analysis", "postExamMentorship"];

    const allSections = Object.keys(remotePricing.features)
      .filter((s) => s !== "lastUpdatedBy" && s !== "_id");

    // Sort sections based on custom order
    const orderedSections = [
      ...sectionOrder.filter(s => allSections.includes(s)),
      ...allSections.filter(s => !sectionOrder.includes(s))
    ];

    const rows = [];
    orderedSections.forEach((section) => {
      const sectionFeatures = remotePricing.features[section] || {};
      Object.keys(sectionFeatures).forEach((featureKey) => {
        const freeVal = sectionFeatures[featureKey]["pgfree"];
        const basicVal = sectionFeatures[featureKey]["pgbasic"];
        const premiumVal = sectionFeatures[featureKey]["pgpremium"];

        rows.push({
          section: insertSpacesInCamelCase(section),
          feature: insertSpacesInCamelCase(featureKey),
          pgfree: freeVal,
          pgbasic: basicVal,
          pgpremium: premiumVal,
        });
      });
    });

    return rows;
  }, [remotePricing]);

  // Group features by section
  const groupedFeatures = useMemo(() => {
    const grouped = {};
    featureRows.forEach((row) => {
      if (!grouped[row.section]) {
        grouped[row.section] = [];
      }
      grouped[row.section].push(row);
    });
    return grouped;
  }, [featureRows]);

  if (!remotePricing) {
    return (
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center">Loading pricing information...</div>
      </section>
    );
  }

  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      {/* Header */}
      <div className="text-center mb-12">
        <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3">
          Choose What Best <span className="text-[#ff9245]">For You</span>
        </h2>
        <p className="text-sm sm:text-lg text-gray-600">
          Plans for every NEET PG Counselling aspirant—select the right one and take a step toward your dream seat.

        </p>
      </div>

      {/* Pricing Cards */}
      <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
        {/* Basic Card - LEFT SIDE */}
        <div className="relative rounded-3xl overflow-hidden shadow-xl"
          style={{ background: 'linear-gradient(to bottom, #e8f5f4 0%, #d4eeec 100%)' }}>
          <div className="p-8">

            {/* Logo */}
            <div className="flex flex-col items-center justify-center mb-6">
              <Image
                src={tierLogos.pgbasic}
                alt="basic logo"
                width={48}
                height={48}
                className="mb-3"
                sizes="48px"
                priority={true}
                fetchPriority="high"
              />
              <h3 className="text-3xl font-bold" style={{ color: '#0e605b' }}>Basic</h3>
            </div>


            {/* Pricing */}
            <div className="mb-4 text-center">
              <div className="flex items-baseline gap-2 justify-center">
                <span className="text-xl font-semibold text-gray-400 line-through mr-2">₹4999</span>
                <span className="text-5xl font-bold" style={{ color: '#0e605b' }}>₹{priceBasic}</span>
                <span className="text-sm text-gray-600">+GST</span>
              </div>
            </div>


            {/* Key Features */}
            <div className="bg-white rounded-2xl p-6 mb-6 shadow-sm">
              <h4 className="text-sm font-bold text-gray-700 mb-4">KEY FEATURES</h4>
              <div className="space-y-3">
                {Object.keys(groupedFeatures).map((sectionName) =>
                  groupedFeatures[sectionName]
                    .filter(row => row.pgbasic === "Yes")
                    .map((row, idx) => (
                      <div key={`basic-${sectionName}-${idx}`} className="flex items-start gap-3">
                        <div className="flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-bold mt-0.5"
                          style={{ backgroundColor: '#0e605b' }}>
                          •
                        </div>
                        <p className="text-sm text-gray-700 leading-relaxed">
                          {row.feature}
                        </p>
                      </div>
                    ))
                )}
              </div>
            </div>

            {/* CTA Button */}
            <button
              onClick={() => handleCheckout("pgbasic")}
              className="w-full text-white font-semibold py-4 px-6 rounded-full transition duration-300 shadow-md"
              style={{ backgroundColor: '#0e605b' }}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#0b4d4a'}
              onMouseLeave={(e) => e.target.style.backgroundColor = '#0e605b'}
            >
              Purchase now
            </button>
          </div>
        </div>

        {/* Premium Card - RIGHT SIDE */}
        <div className="relative rounded-3xl overflow-hidden shadow-xl"
          style={{ background: 'linear-gradient(to bottom, #FFF5F0 0%, #FFEEE5 100%)' }}>
          <div className="p-6">
            {/* Card Header */}


            {/* Logo */}
            <div className="flex flex-col items-center justify-center mb-6">
              <Image
                src={tierLogos.pgpremium}
                alt="premium logo"
                width={48}
                height={48}
                className="mb-3"
                sizes="48px"
                priority={true}
                fetchPriority="high"
              />
              <h3 className="text-2xl font-bold text-orange-500">Premium</h3>
            </div>

            {/* Pricing */}
            <div className="mb-4 text-center">
              <div className="flex items-baseline gap-2 justify-center">
                <span className="text-4xl font-bold text-orange-500">₹{pricePremium}</span>
                <span className="text-xs text-gray-600">+GST</span>
              </div>

            </div>



            {/* Key Features */}
            <div className="bg-white rounded-2xl p-4 mb-4 shadow-sm min-h-[300px]">
              <h4 className="text-xs font-bold text-gray-700 mb-3">KEY FEATURES</h4>
              <div className="space-y-2">
                {Object.keys(groupedFeatures).map((sectionName) =>
                  groupedFeatures[sectionName]
                    .filter(row => row.pgpremium === "Yes")
                    .map((row, idx) => (
                      <div key={`premium-${sectionName}-${idx}`} className="flex items-start gap-2">
                        <div className="flex-shrink-0 w-5 h-5 rounded-full bg-orange-500 flex items-center justify-center text-white text-xs font-bold mt-0.5">
                          •
                        </div>
                        <p className="text-xs text-gray-700 leading-relaxed">
                          {row.feature}
                        </p>
                      </div>
                    ))
                )}
              </div>
            </div>

            {/* CTA Button */}
            <button
              onClick={() => handleCheckout("pgpremium")}
              className="w-full bg-[#ff9245] hover:bg-[#fb923c] text-white font-semibold py-3 px-4 rounded-full transition duration-300 shadow-md text-sm"
            >
              Purchase now
            </button>
          </div>
        </div>
      </div>

      {/* Footer Note
      <div className="mt-8 text-center text-sm text-gray-600">
        <p>Please note: Once payment is made, it is non-refundable.</p>
      </div> */}
    </section>
  );
}
